import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import { toast } from 'react-toastify';
import MessageTemplatePreview, { TYPE_META } from '../../components/MessageTemplatePreview';

export default function UserBatchView() {
  const { id: campaignId, batchId } = useParams();
  const navigate = useNavigate();
  const [batch, setBatch] = useState(null);
  const [schedule, setSchedule] = useState(null);
  const [campaignInfo, setCampaignInfo] = useState(null);
  const [data, setData] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [showPreview, setShowPreview] = useState(false);

  useEffect(() => {
    api.get(`/user/campaigns/${campaignId}/batch/${batchId}?page=${page}&search=${search}`)
      .then(({ data: res }) => {
        setBatch(res.batch);
        setSchedule(res.schedule || null);
        setCampaignInfo(res.campaign || null);
        setData(res.data);
        setTotalPages(res.pages || 1);
        setLoading(false);
      })
      .catch(() => { toast.error('Failed to load batch'); setLoading(false); });
  }, [campaignId, batchId, page, search]);

  const handleExport = async (format) => {
    setExporting(true);
    try {
      const endpoint = format === 'csv'
        ? `/user/campaigns/${campaignId}/batch/${batchId}/export-csv`
        : `/user/campaigns/${campaignId}/batch/${batchId}/export-json`;
      const response = await api.get(endpoint, {
        responseType: format === 'csv' ? 'blob' : 'json'
      });
      if (format === 'csv') {
        const blob = new Blob([response.data], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url; a.download = `campaign_${campaignId}_data.csv`;
        a.click(); window.URL.revokeObjectURL(url);
      } else {
        const blob = new Blob([JSON.stringify(response.data, null, 2)], { type: 'application/json' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url; a.download = `campaign_${campaignId}_data.json`;
        a.click(); window.URL.revokeObjectURL(url);
      }
      toast.success(`Exported as ${format.toUpperCase()}`);
    } catch { toast.error('Export failed'); }
    finally { setExporting(false); }
  };

  if (loading) return (
    <div className="loading-spinner">
      <div className="spinner" />
      <div className="loading-text">Loading...</div>
      <div className="loading-dots"><span /><span /><span /></div>
    </div>
  );

  return (
    <div>
      <div className="page-header">
        <div>
          <button className="btn btn-secondary" onClick={() => navigate(`/user/campaigns/${campaignId}`)} style={{ marginBottom: 8 }}>← Back to Campaign</button>
          <h1 className="page-title">Batch #{batch?.batchNumber}</h1>
          <p className="page-subtitle">
            Status: <span className={`badge badge-${batch?.status}`}>{batch?.status}</span>
            {' • '}Size: {batch?.batchSize?.toLocaleString()}
            {batch?.scheduledFor && ` • Scheduled: ${new Date(batch.scheduledFor).toLocaleString()}`}
            {batch?.sentAt && ` • Sent: ${new Date(batch.sentAt).toLocaleString()}`}
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn-primary" onClick={() => handleExport('csv')} disabled={exporting}>
            Export CSV
          </button>
        </div>
      </div>

      {/* View Preview Button */}
      {schedule && (() => {
        const cType = campaignInfo?.type || 'sms';
        const meta = TYPE_META[cType] || TYPE_META.sms;
        return (
          <>
            <div className="card" style={{ marginBottom: 20, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <span style={{ color: meta.color, fontSize: 18 }}>{meta.icon}</span>
                <h3 className="card-title" style={{ margin: 0 }}>Message Sent</h3>
                <span className="badge" style={{ background: meta.color, color: '#fff', fontSize: 11 }}>{meta.label}</span>
                <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>From: <strong style={{ color: meta.color }}>{schedule.senderId}</strong></span>
              </div>
              <button className="btn btn-primary" style={{ background: meta.gradient || meta.color, border: 'none', display: 'flex', alignItems: 'center', gap: 6, color: '#fff' }}
                onClick={() => setShowPreview(true)}>
                View Preview
              </button>
            </div>

            {/* Preview Modal */}
            {showPreview && ReactDOM.createPortal(
              <div style={{ position: 'fixed', inset: 0, zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(15,23,42,0.6)', backdropFilter: 'blur(8px)' }}
                onClick={() => setShowPreview(false)}>
                <button onClick={() => setShowPreview(false)}
                  style={{ position: 'fixed', top: 18, right: 18, zIndex: 10001, width: 40, height: 40, borderRadius: '50%', background: '#fff', border: '2px solid #e2e8f0', boxShadow: '0 4px 16px rgba(0,0,0,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', fontSize: 20, color: '#64748b', fontWeight: 700 }}>×</button>
                <div onClick={e => e.stopPropagation()} style={{ transform: 'scale(0.82)', transformOrigin: 'center center' }}>
                  <MessageTemplatePreview type={cType} sched={schedule} campaignName={campaignInfo?.name} />
                </div>
              </div>,
              document.body
            )}
          </>
        );
      })()}

      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h3 className="card-title" style={{ margin: 0 }}>Batch Data (Masked)</h3>
          <input type="text" className="form-input" placeholder="Search..." style={{ width: 250 }}
            value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} />
        </div>

        <div className="table-container">
          <table className="data-table">
            <thead>
              <tr><th>#</th><th>Mobile</th><th>Name</th><th>Email</th><th>Address</th><th>Status</th></tr>
            </thead>
            <tbody>
              {data.map((d, i) => (
                <tr key={d._id || i}>
                  <td>{(page - 1) * 20 + i + 1}</td>
                  <td style={{ fontFamily: 'monospace' }}>{d.masterDataId?.mobile || d.mobile || '-'}</td>
                  <td>{d.masterDataId?.name || d.name || '-'}</td>
                  <td>{d.masterDataId?.email || d.email || '-'}</td>
                  <td>{d.masterDataId?.address || d.address || '-'}</td>
                  <td><span className={`badge badge-${d.status}`}>{d.status}</span></td>
                </tr>
              ))}
              {data.length === 0 && (
                <tr><td colSpan="6" style={{ textAlign: 'center', padding: 30, color: 'var(--text-muted)' }}>No records found</td></tr>
              )}
            </tbody>
          </table>
        </div>

        {totalPages > 1 && (
          <div className="pagination">
            <button className="btn btn-secondary" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>Previous</button>
            <div style={{ display: 'flex', gap: 4 }}>
              {Array.from({ length: Math.min(totalPages, 7) }, (_, i) => {
                let p;
                if (totalPages <= 7) p = i + 1;
                else if (page <= 4) p = i + 1;
                else if (page >= totalPages - 3) p = totalPages - 6 + i;
                else p = page - 3 + i;
                return (
                  <button key={p} className={`btn ${p === page ? 'btn-primary' : 'btn-secondary'}`}
                    style={{ minWidth: 36, padding: '6px 0' }} onClick={() => setPage(p)}>
                    {p}
                  </button>
                );
              })}
            </div>
            <button className="btn btn-secondary" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>Next</button>
          </div>
        )}
      </div>
    </div>
  );
}
